import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Siren, MapPin, Phone, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useClientAuth } from "@/context/ClientAuthContext";
import { triggerDepannage, TEL_NUMBER } from "@/lib/depannage";

export function EmergencyDepannage({ variant = "nav", className = "" }) {
  const { t, lang } = useLang();
  const { user } = useClientAuth();
  const [open, setOpen] = useState(false);

  const send = () => { triggerDepannage(t, user || {}, lang); setOpen(false); };

  const label = t("depannage.nav");
  const btnBase = "inline-flex items-center justify-center gap-2 font-display font-700 uppercase tracking-wide text-white transition-colors";

  let trigger;
  if (variant === "card") {
    trigger = (
      <button onClick={(e) => { e.stopPropagation(); setOpen(true); }} data-testid="depannage-card-btn"
        className={`${btnBase} depannage-pulse w-full mt-1 px-4 py-3 bg-[#E60000] border border-[#E60000] hover:bg-[#c00] text-sm ${className}`}>
        <Siren className="w-4 h-4" /> {t("depannage.card_btn")}
      </button>
    );
  } else if (variant === "navMobile") {
    trigger = (
      <button onClick={() => setOpen(true)} data-testid="nav-mobile-depannage"
        className={`${btnBase} depannage-pulse w-full px-5 py-3 rounded-full bg-[#E60000] ${className}`}>
        <Siren className="w-4 h-4" /> {label}
      </button>
    );
  } else {
    trigger = (
      <button onClick={() => setOpen(true)} data-testid="nav-depannage"
        className={`${btnBase} depannage-pulse px-4 h-10 rounded-full bg-[#E60000] text-xs ${className}`}>
        <Siren className="w-4 h-4" /> {label}
      </button>
    );
  }

  return (
    <>
      {trigger}
      {createPortal(
        <AnimatePresence>
          {open && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center px-5" data-testid="depannage-modal">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
              <motion.div initial={{ opacity: 0, scale: 0.94, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 8 }} transition={{ type: "spring", stiffness: 300, damping: 26 }}
                className="relative w-full max-w-md bg-[#0C0C0E] border border-[#E60000]/40 shadow-[0_0_40px_rgba(230,0,0,0.25)] p-8">
                <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" data-testid="depannage-close"><X className="w-5 h-5" /></button>
                <div className="flex justify-center mb-5">
                  <span className="relative flex items-center justify-center w-16 h-16">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#E60000]/40 animate-ping" />
                    <span className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E60000] border border-[#FF3B3B]"><Siren className="w-7 h-7 text-white" /></span>
                  </span>
                </div>
                <h3 className="text-center font-display font-900 text-white text-2xl uppercase tracking-tight">{t("depannage.modal_title")}</h3>
                <p className="text-center text-zinc-400 text-sm mt-3 mb-7 leading-relaxed">{t("depannage.modal_sub")}</p>
                <button onClick={send} data-testid="depannage-send-location" className="w-full inline-flex items-center justify-center gap-2 py-4 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors">
                  <MapPin className="w-5 h-5" /> {t("depannage.modal_go")}
                </button>
                <a href={`tel:${TEL_NUMBER}`} data-testid="depannage-call" className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3.5 border border-white/15 text-white font-mono-tech text-xs uppercase tracking-[0.14em] hover:border-white/40 transition-colors">
                  <Phone className="w-4 h-4" /> {t("depannage.modal_call")}
                </a>
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
